<template>
	<div>
		<main class="container-xl py-3">

			<!-- Resume banner — surfaces the rep's most recent draft so jumping
				between Lookup / Pricing / Quote Builder doesn't feel like lost work. -->
			<router-link v-if="resumeRow" :to="`/cwpe/quotes/${resumeRow.id}`"
				class="d-block rounded shadow-sm mb-3 p-3 text-decoration-none"
				style="border:2px solid #93c5fd;background:#eff6ff;color:inherit;">
				<div class="d-flex align-items-center justify-content-between gap-3">
					<div>
						<div class="text-uppercase fw-bold" style="font-size:10px;letter-spacing:0.05em;color:#1d4ed8;">Resume where you left off</div>
						<div class="fw-semibold text-dark mt-1" style="font-size:16px;">{{ resumeRow.title }}</div>
						<div class="mt-1" style="font-size:11px;color:#475569;">
							{{ resumeRow.municipality_name_raw }}, {{ resumeRow.state }} · {{ resumeRow.bundle_id }} · {{ resumeRow.horizon_years }}-yr
							<span class="ms-1" style="color:#94a3b8;">· last edit {{ formatRelative(resumeRow.updated_at) }}</span>
						</div>
					</div>
					<span class="fw-semibold flex-shrink-0" style="color:#1d4ed8;font-size:14px;">Continue →</span>
				</div>
			</router-link>

			<!-- Owner-controls notice -->
			<div class="rounded mb-3 px-3 py-2" style="background:#fef3c7;border:1px solid #fcd34d;font-size:12px;color:#78350f;">
				<span class="fw-semibold">Role-gating coming soon</span> — Quote Builder creates and persists customer-facing pricing; treat it as owner-only for now.
			</div>

			<!-- Header + new -->
			<div class="d-flex align-items-baseline justify-content-between mb-3 gap-2">
				<h2 class="h5 fw-semibold mb-0 text-dark">Saved quotes</h2>
				<router-link to="/cwpe/quotes/new"
					class="btn btn-sm btn-primary fw-medium" style="font-size:13px;padding:6px 12px;">
					+ New quote
				</router-link>
			</div>

			<div v-if="loading" class="text-center py-4">
				<div class="spinner-border spinner-border-sm" role="status"></div>
			</div>

			<div v-else-if="!quotes.length" class="rounded bg-white text-center py-5"
				style="border:1px solid #e2e8f0;font-size:14px;color:#64748b;">
				No quotes yet. Click "+ New quote" to build your first.
			</div>

			<div v-else class="rounded bg-white overflow-hidden mb-3" style="border:1px solid #e2e8f0;">
				<table class="table table-sm table-hover mb-0">
					<thead style="background:#f8fafc;">
						<tr style="font-size:12px;color:#475569;">
							<th class="text-start fw-medium px-3 py-2">Title</th>
							<th class="text-start fw-medium px-2 py-2">Muni</th>
							<th class="text-end fw-medium px-2 py-2">Pop</th>
							<th class="text-center fw-medium px-2 py-2">Bundle</th>
							<th class="text-center fw-medium px-2 py-2">Term</th>
							<th class="text-start fw-medium px-2 py-2">Updated</th>
							<th style="width:32px;"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="q in quotes" :key="q.id" style="border-top:1px solid #e2e8f0;font-size:14px;">
							<td class="px-3 py-2">
								<router-link :to="`/cwpe/quotes/${q.id}`" class="fw-medium text-decoration-none" style="color:#1d4ed8;">
									{{ titleFor(q) }}
								</router-link>
								<div v-if="q.notes" class="text-truncate mt-1" style="font-size:11px;color:#64748b;max-width:32ch;">{{ q.notes }}</div>
							</td>
							<td class="px-2 py-2" style="color:#334155;">{{ q.municipality_name_raw }}, {{ q.state }}</td>
							<td class="px-2 py-2 text-end font-monospace" style="font-size:12px;color:#475569;">
								{{ q.population != null ? Number(q.population).toLocaleString() : '—' }}
							</td>
							<td class="px-2 py-2 text-center">
								<span class="rounded text-uppercase fw-bold px-2 py-1" style="font-size:10px;background:#f1f5f9;color:#1e293b;">{{ q.bundle_id }}</span>
							</td>
							<td class="px-2 py-2 text-center" style="font-size:12px;color:#475569;">{{ q.horizon_years }}-yr</td>
							<td class="px-2 py-2" style="font-size:12px;color:#64748b;">{{ formatRelative(q.updated_at) }}</td>
							<td class="px-2 py-2 text-center">
								<button type="button" @click="askDelete(q)"
									class="btn btn-link p-0" style="color:#cbd5e1;font-size:18px;line-height:1;text-decoration:none;"
									title="Delete">×</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div v-if="!loading && quotes.length" class="d-flex justify-content-between align-items-center" style="font-size:12px;color:#64748b;">
				<span>{{ total }} total</span>
				<div class="d-flex gap-1">
					<button class="btn btn-sm btn-outline-secondary" :disabled="offset === 0" @click="prevPage">Prev</button>
					<button class="btn btn-sm btn-outline-secondary" :disabled="offset + limit >= total" @click="nextPage">Next</button>
				</div>
			</div>
		</main>

		<!-- Delete confirmation modal -->
		<div v-if="pendingDelete" class="modal fade show d-block" tabindex="-1" @click.self="cancelDelete">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Delete quote?</h5>
						<button type="button" class="btn-close" :disabled="deleting" @click="cancelDelete"></button>
					</div>
					<div class="modal-body">
						<p class="mb-1" style="font-size:14px;color:#334155;">
							Quote for <strong>{{ pendingDelete.municipality_name_raw }}, {{ pendingDelete.state }}</strong> will be permanently deleted.
						</p>
						<p class="mb-0" style="font-size:12px;color:#64748b;">
							This cannot be undone.
						</p>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="deleting" @click="cancelDelete">Cancel</button>
						<button type="button" class="btn btn-sm btn-danger" :disabled="deleting" @click="confirmDelete">
							<span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
							Delete
						</button>
					</div>
				</div>
			</div>
		</div>
		<div v-if="pendingDelete" class="modal-backdrop fade show"></div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'

const ACTIVE_QUOTE_KEY = 'cwpe_active_quote'

export default {
	name: 'CwpeQuotes',
	data() {
		return {
			quotes: [],
			total: 0,
			limit: 50,
			offset: 0,
			loading: false,
			activeQuote: null,
			pendingDelete: null,
			deleting: false,
		}
	},
	computed: {
		resumeRow() {
			if (!this.activeQuote) return null
			return this.quotes.find(r => r.id === this.activeQuote.id) || null
		},
	},
	created() {
		this.loadActiveQuote()
		this.load()
	},
	methods: {
		loadActiveQuote() {
			const raw = localStorage.getItem(ACTIVE_QUOTE_KEY)
			this.activeQuote = raw ? JSON.parse(raw) : null
		},
		load() {
			this.loading = true
			restApi.get('/cwpe/quotes', { params: { limit: this.limit, offset: this.offset } }).then(({ data }) => {
				this.quotes = data.rows
				this.total = data.total
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Failed to load quotes', text: err?.response?.data?.error || '' })
			}).finally(() => { this.loading = false })
		},
		prevPage() { this.offset = Math.max(0, this.offset - this.limit); this.load() },
		nextPage() { this.offset += this.limit; this.load() },
		titleFor(q) {
			return `${q.municipality_name_raw}, ${q.state} — ${q.bundle_id?.toUpperCase() || ''}`
		},
		formatRelative(val) {
			if (!val) return '—'
			const dt = new Date(val)
			const mins = Math.round((Date.now() - dt.getTime()) / 60000)
			if (mins < 1) return 'just now'
			if (mins < 60) return `${mins}m ago`
			const hrs = Math.round(mins / 60)
			if (hrs < 24) return `${hrs}h ago`
			return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
		},
		askDelete(quote) {
			this.pendingDelete = quote
		},
		cancelDelete() {
			if (this.deleting) return
			this.pendingDelete = null
		},
		confirmDelete() {
			if (!this.pendingDelete || this.deleting) return
			const quote = this.pendingDelete
			this.deleting = true
			restApi.delete(`/cwpe/quotes/${quote.id}`).then(() => {
				if (this.activeQuote && this.activeQuote.id === quote.id) {
					localStorage.removeItem(ACTIVE_QUOTE_KEY)
					this.activeQuote = null
				}
				this.pendingDelete = null
				this.load()
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Delete failed', text: err?.response?.data?.error || '' })
			}).finally(() => { this.deleting = false })
		},
	},
}
</script>
